'use client';

import { ChangeEvent } from 'react';
import clsx from 'clsx';
import { Component } from '@headless-workspace/typings';
import { GlowIcon, GlowTextBody, getIcon, PropsWithFontFamily, PropsWithClassName } from '../../../../src';

export type PillWithIcon = {
    id: string;
    label: string;
    iconName: string;
};

export type GlowPillWithIconProps = Partial<PropsWithFontFamily> &
    PropsWithClassName & {
        type: 'radio' | 'checkbox';
        content: PillWithIcon;
        checked: boolean;
        onChange: (id: string, checked: boolean) => void;
    };

export const GlowPillWithIcon: Component<GlowPillWithIconProps> = ({
    type,
    content: { id, label, iconName },
    checked,
    onChange,
    fontFamily = 'brand',
    className,
}) => {
    const onChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        onChange(id, event.target.checked);
    };

    return (
        <div
            className={clsx(
                // CSS to create an “internal border” => prevents offset
                'inline-flex relative before:absolute',
                'pointer-events-none',
                'before:w-full before:h-full',
                'before:rounded-infinity before:border-border-selected',
                checked && 'before:border-bold',
                'w-full',
                'has-[:focus-visible]:outline-thin has-[:focus-visible]:outline has-[:focus-visible]:rounded-infinity',
            )}
        >
            <label
                className={clsx(
                    'flex flex-row',
                    'px-1 py-0.75',
                    'align-middle',
                    'pointer-events-auto',
                    'rounded-infinity border-solid border-medium border-border-primary',
                    checked ? 'bg-surface-primary' : 'bg-background-neutral',
                    'hover:bg-surface-neutral-hover active:bg-surface-neutral-pressed',
                    'items-center gap-0.5 justify-center',
                    className,
                )}
            >
                <input type={type} className={'sr-only'} checked={checked} onChange={onChangeHandler} />

                <GlowIcon Icon={getIcon(iconName)} type={'large'} />
                <GlowTextBody text={label} TagName={'span'} fontFamily={fontFamily} />
            </label>
        </div>
    );
};
